import React, {useCallback, useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import PlatformSectionHeader from "./PlatformSectionHeader";
import PlatformTableBody from "./PlatformTableBody";
import Table from "../../components/table";
import {
  createUserCategory,
  deleteUserCategory,
  selectUserCategories,
  updateUserCategory,
} from "../../services/platform/ManageUserCategoryAxios";

const EMPTY_FORM = {value: "", label: "", description: "", examples: ""};

const createNextCategoryCode = categories => {
  const lastNumber = categories.reduce((maxNumber, category) => {
    const match = /^UCT(\d+)$/.exec(category.value || "");
    return match ? Math.max(maxNumber, Number(match[1])) : maxNumber;
  }, 0);
  return `UCT${String(lastNumber + 1).padStart(2, "0")}`;
};

export default function PlatformCategoryManage() {
  const [categories, setCategories] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pageError, setPageError] = useState("");

  const loadCategories = useCallback(async () => {
    setIsLoading(true);
    setPageError("");
    try {
      setCategories(await selectUserCategories());
    } catch (requestError) {
      setCategories([]);
      setPageError(requestError.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const filteredCategories = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    if (!normalizedKeyword) return categories;
    return categories.filter(category =>
      [category.label, category.description, category.examples]
        .some(value => String(value || "").toLowerCase().includes(normalizedKeyword))
    );
  }, [categories, keyword]);

  const openCreate = () => {
    setEditingCode(null);
    setForm({...EMPTY_FORM, value: createNextCategoryCode(categories)});
    setError("");
    setIsEditorOpen(true);
  };

  const openEdit = category => {
    setEditingCode(category.value);
    setForm({...category});
    setError("");
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingCode(null);
    setForm(EMPTY_FORM);
    setError("");
  };

  const updateForm = event => {
    const {name, value} = event.target;
    setForm(current => ({
      ...current,
      [name]: name === "value" ? value.toUpperCase().replace(/[^A-Z0-9_]/g, "") : value,
    }));
  };

  const saveCategory = async event => {
    event.preventDefault();
    const normalizedForm = Object.fromEntries(
      Object.entries(form).map(([key, value]) => [key, String(value).trim()])
    );
    if (Object.values(normalizedForm).some(value => !value)) {
      setError("모든 항목을 입력해 주세요.");
      return;
    }
    if (!editingCode && categories.some(category => category.value === normalizedForm.value)) {
      setError("이미 사용 중인 카테고리 코드입니다.");
      return;
    }

    setIsSaving(true);
    try {
      if (editingCode) {
        await updateUserCategory({...form, ...normalizedForm});
      } else {
        await createUserCategory(normalizedForm);
      }
      await loadCategories();
      closeEditor();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteCategory = async category => {
    if (!window.confirm(`'${category.label}' 카테고리를 삭제하시겠습니까?`)) return;
    try {
      await deleteUserCategory(category.id);
      await loadCategories();
    } catch {
      window.alert("사용 중인 카테고리는 삭제할 수 없습니다.");
    }
  };

  const categoryColumns = [
    {name:"label",header:"카테고리",minWidth:170,render:({value})=><CategoryName>{value}</CategoryName>},
    {name:"description",header:"정의",minWidth:280,flex:1},
    {name:"examples",header:"포함 가능한 매체",minWidth:300,flex:1},
    {name:"actions",header:"관리",width:120,textAlign:"center",sortable:false,render:({data})=><RowActions><EditButton type="button" onClick={event=>{event.stopPropagation();openEdit(data);}}>수정</EditButton><DeleteButton type="button" onClick={event=>{event.stopPropagation();deleteCategory(data);}}>삭제</DeleteButton></RowActions>},
  ];

  return (
    <Page>
      <Card>
        <PlatformSectionHeader title="카테고리 목록"
          description="분류 정의와 포함 가능한 매체를 확인하고 수정할 수 있습니다."
          count={filteredCategories.length}
          unit="개"
          action={<PrimaryButton type="button" onClick={openCreate}>카테고리 등록</PrimaryButton>}
        />
        <Toolbar>
          <SearchInput
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
            placeholder="카테고리명, 정의 또는 포함 매체 검색"
          />
          <ToolbarActions>
            <SecondaryButton type="button" onClick={loadCategories}>새로고침</SecondaryButton>
          </ToolbarActions>
        </Toolbar>
        <CategoryTableBody>
          {pageError && <PageError><span>{pageError}</span><button type="button" onClick={loadCategories}>다시 시도</button></PageError>}
          <Table columns={categoryColumns} data={isLoading?[]:filteredCategories}
            idProperty="value" headerHeight={48}
            emptyText={isLoading?"카테고리를 불러오는 중입니다.":"등록된 카테고리가 없습니다."}
            pagination limit={10} defaultLimit={10}
            onRowClick={({data})=>openEdit(data)}/>
        </CategoryTableBody>
      </Card>

      {isEditorOpen && (
        <ModalOverlay role="presentation" onMouseDown={event => event.target === event.currentTarget && closeEditor()}>
          <EditorCard role="dialog" aria-modal="true" aria-labelledby="category-editor-title">
            <EditorHeader><div><strong id="category-editor-title">{editingCode ? "카테고리 수정" : "카테고리 등록"}</strong><span>사용자 기본정보에 노출할 분류 정보를 입력해 주세요.</span></div><CloseButton type="button" onClick={closeEditor} aria-label="닫기">×</CloseButton></EditorHeader>
            <EditorForm onSubmit={saveCategory}>
              <Field><label htmlFor="category-label">카테고리명 <Required>*</Required></label><input id="category-label" name="label" value={form.label} onChange={updateForm} placeholder="예: 라디오·방송"/></Field>
              <Field><label htmlFor="category-code">카테고리 코드</label><input id="category-code" name="value" value={form.value} readOnly/><small>DB에 등록된 마지막 UCT 번호를 기준으로 자동 생성됩니다.</small></Field>
              <Field><label htmlFor="category-description">정의 <Required>*</Required></label><textarea id="category-description" name="description" value={form.description} onChange={updateForm} placeholder="카테고리 분류 기준을 입력해 주세요."/></Field>
              <Field><label htmlFor="category-examples">포함 가능한 매체 <Required>*</Required></label><textarea id="category-examples" name="examples" value={form.examples} onChange={updateForm} placeholder="쉼표로 구분하여 입력해 주세요."/></Field>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <EditorFooter><SecondaryButton type="button" onClick={closeEditor} disabled={isSaving}>취소</SecondaryButton><PrimaryButton type="submit" disabled={isSaving}>{isSaving ? "저장 중" : editingCode ? "수정" : "등록"}</PrimaryButton></EditorFooter>
            </EditorForm>
          </EditorCard>
        </ModalOverlay>
      )}
    </Page>
  );
}

const Page=styled.div`display:flex;width:100%;flex-direction:column;gap:16px;`;
const Card=styled.section`overflow:hidden;border:1px solid var(--color-border);border-radius:10px;background:var(--color-surface);box-shadow:0 5px 18px rgba(31,41,55,.045);`;
const Toolbar=styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;padding:16px 20px;border-top:1px solid var(--color-divider);@media(max-width:720px){align-items:stretch;flex-direction:column;}`;
const SearchInput=styled.input`width:min(520px,100%);height:40px;box-sizing:border-box;padding:0 13px;border:1px solid var(--color-border);border-radius:8px;background:#fff;color:var(--color-text);font-size:var(--font-size-13);&:focus{border-color:var(--color-primary);outline:0;box-shadow:0 0 0 3px var(--color-primary-opacity-12);}`;
const ToolbarActions=styled.div`display:flex;gap:8px;`;
const buttonStyle=`height:40px;padding:0 16px;border-radius:8px;font-size:var(--font-size-13);font-weight:700;cursor:pointer;`;
const PrimaryButton=styled.button`${buttonStyle}border:1px solid var(--color-primary);background:var(--color-primary);color:#fff;&:hover{background:var(--color-primary-dark);}`;
const SecondaryButton=styled.button`${buttonStyle}border:1px solid var(--color-border);background:#fff;color:var(--color-text);&:hover{border-color:var(--color-primary-border);background:var(--color-primary-soft);}`;
const CategoryTableBody=styled(PlatformTableBody)`
  && .InovuaReactDataGrid__cell {border-color:transparent!important;}
`;
const CategoryName=styled.strong`color:var(--color-text);font-size:var(--font-size-13);font-weight:750;`;
const RowActions=styled.div`display:flex;justify-content:center;gap:5px;`;
const EditButton=styled.button`height:30px;padding:0 10px;border:1px solid var(--color-primary-border);border-radius:6px;background:var(--color-primary-soft);color:var(--color-primary-strong);font-size:var(--font-size-11);font-weight:700;cursor:pointer;`;
const DeleteButton=styled.button`height:30px;padding:0 10px;border:1px solid #f2c2c2;border-radius:6px;background:#fff3f3;color:#bf3030;font-size:var(--font-size-11);font-weight:700;cursor:pointer;`;
const PageError=styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px;padding:11px 13px;border:1px solid #f2c2c2;border-radius:7px;background:#fff3f3;color:#bf3030;font-size:var(--font-size-12);button{height:30px;padding:0 10px;border:1px solid #e6a5a5;border-radius:6px;background:#fff;color:#bf3030;font-size:var(--font-size-11);font-weight:700;cursor:pointer;}`;
const ModalOverlay=styled.div`position:fixed;inset:0;z-index:1200;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(20,27,38,.52);backdrop-filter:blur(2px);`;
const EditorCard=styled.div`width:min(680px,100%);overflow:hidden;border-top:3px solid var(--color-primary);border-radius:11px;background:#fff;box-shadow:0 24px 70px rgba(15,23,42,.3);`;
const EditorHeader=styled.header`display:flex;min-height:70px;align-items:center;justify-content:space-between;gap:16px;padding:0 22px;border-bottom:1px solid var(--color-divider);strong,span{display:block;}strong{color:var(--color-text);font-size:var(--font-size-17);font-weight:750;}span{margin-top:5px;color:var(--color-text-muted);font-size:var(--font-size-11);}`;
const CloseButton=styled.button`border:0;background:transparent;color:#7d8792;font-size:26px;line-height:1;cursor:pointer;`;
const EditorForm=styled.form`padding:20px 22px;`;
const Field=styled.div`display:grid;grid-template-columns:140px minmax(0,1fr);align-items:start;gap:10px 14px;margin-bottom:15px;label{padding-top:11px;color:#444;font-size:var(--font-size-13);font-weight:700;}input,textarea{width:100%;box-sizing:border-box;border:1px solid var(--color-border);border-radius:8px;color:var(--color-text);font-size:var(--font-size-13);font-family:inherit;}input{height:42px;padding:0 12px;}textarea{min-height:82px;padding:11px 12px;resize:vertical;line-height:1.55;}input:focus,textarea:focus{border-color:var(--color-primary);outline:0;box-shadow:0 0 0 3px var(--color-primary-opacity-12);}input[readonly]{background:#f5f7f9;color:var(--color-text-muted);}small{grid-column:2;color:var(--color-text-muted);font-size:var(--font-size-10);}@media(max-width:600px){grid-template-columns:1fr;label{padding-top:0;}small{grid-column:1;}}`;
const Required=styled.span`color:var(--color-danger-strong);`;
const ErrorMessage=styled.div`margin:2px 0 14px;padding:10px 12px;border:1px solid #f2c2c2;border-radius:7px;background:#fff3f3;color:#bf3030;font-size:var(--font-size-12);`;
const EditorFooter=styled.footer`display:flex;justify-content:flex-end;gap:8px;margin:20px -22px -20px;padding:16px 22px;border-top:1px solid var(--color-divider);background:#fafbfc;`;
